import {authService} from './authService';
import type { LogEntryDto, LoggerInfoDto, SetLogLevelRequest, LogStatsDto, PropertiesResponseDto, SqlQueryResultDto, SavedSqlScriptDto, CreateSavedSqlScriptRequest, UpdateSavedSqlScriptRequest } from './adminApi.types';

// Re-export types for convenience
export type { LogEntryDto, LoggerInfoDto, SetLogLevelRequest, LogStatsDto, PropertiesResponseDto, PropertyResolutionDto, SqlQueryResultDto, SavedSqlScriptDto, CreateSavedSqlScriptRequest, UpdateSavedSqlScriptRequest } from './adminApi.types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

export interface PageResponse<T> {
    content: T[];
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
}

export interface RecentUserDto {
    id: string;
    name: string;
    email: string;
    authProvider: string;
    lastLogin: [number, number, number, number, number, number]; // LocalDateTime array format
    conversationCount: number;
    messageCount: number;
}

export interface RecentConversationDto {
    id: string;
    title: string;
    userName: string;
    userEmail: string;
    messageCount: number;
    createdAt: [number, number, number, number, number, number];
    updatedAt: [number, number, number, number, number, number];
    conversationUrl: string;
}

export interface RecentFeedbackDto {
    id: string;
    conversationId: string;
    messageId: string;
    feedbackType: string;
    feedbackText: string | null;
    userName: string;
    userEmail: string;
    createdAt: [number, number, number, number, number, number];
    conversationUrl: string;
}

export interface SystemStatisticsDto {
    totalUsers: number;
    totalConversations: number;
    totalMessages: number;
    activeUsersLast24Hours: number;
    activeUsersLast7Days: number;
    conversationsLast24Hours: number;
    conversationsLast7Days: number;
}

export interface VersionInfoDto {
    version: string;
    commitSha: string;
    githubUrl: string | null;
}

export interface ExternalIdStatisticsDto {
    byType: Record<string, ServiceStatistics>;
    totalUnmappedEntities: number;
    totalNotFoundEntities: number;
}

export interface ServiceStatistics {
    entityType: string;
    totalEntities: number;
    unmappedByService: Record<string, number>;
    notFoundByService: Record<string, number>;
}

export interface ExternalIdMappingDto {
    id: number;
    serviceName: string;
    serviceId: string;
    serviceNameValue: string | null;
}

export interface ExternalIdSearchResultDto {
    entityType: string;
    rebrickableId: string;
    entityName: string;
    mappings: ExternalIdMappingDto[];
    hasBricklink: boolean;
    hasBrickset: boolean;
}

export interface TranslateExternalIdRequest {
    entityType: string;
    rebrickableId: string;
    serviceName: string;
}

export interface TranslateExternalIdResponse {
    found: boolean;
    serviceId: string | null;
    serviceNameValue: string | null;
    message: string;
}

export interface BatchUpdateExternalIdRequest {
    updates: ExternalIdUpdateItem[];
}

export interface ExternalIdUpdateItem {
    entityType: string;
    rebrickableId: string;
    serviceName: string;
    serviceId: string;
    serviceNameValue: string | null;
}

export interface BatchUpdateExternalIdResponse {
    successCount: number;
    failureCount: number;
    totalCount: number;
    message: string;
}

async function fetchWithAuth(url: string, options?: RequestInit): Promise<Response> {
    const token = authService.getToken();
    if (!token) {
        throw new Error('Not authenticated');
    }

    const response = await fetch(url, {
        ...options,
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            ...options?.headers
        }
    });

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response;
}

export interface SortParams {
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
}

export interface UserFilters {
    nameFilter?: string;
    emailFilter?: string;
    providerFilter?: string;
}

export interface ConversationFilters {
    titleFilter?: string;
    userNameFilter?: string;
    userEmailFilter?: string;
}

export interface FeedbackFilters {
    type?: string;
    userNameFilter?: string;
    userEmailFilter?: string;
}

export const adminApi = {
    async getRecentUsers(page = 0, size = 20, sort?: SortParams, filters?: UserFilters): Promise<PageResponse<RecentUserDto>> {
        const params = new URLSearchParams({
            page: page.toString(),
            size: size.toString(),
            ...(sort?.sortBy && { sortBy: sort.sortBy }),
            ...(sort?.sortDir && { sortDir: sort.sortDir }),
            ...(filters?.nameFilter && { nameFilter: filters.nameFilter }),
            ...(filters?.emailFilter && { emailFilter: filters.emailFilter }),
            ...(filters?.providerFilter && { providerFilter: filters.providerFilter }),
        });
        const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/users?${params}`);
        return response.json();
    },

    async getRecentConversations(page = 0, size = 20, sort?: SortParams, filters?: ConversationFilters): Promise<PageResponse<RecentConversationDto>> {
        const params = new URLSearchParams({
            page: page.toString(),
            size: size.toString(),
            ...(sort?.sortBy && { sortBy: sort.sortBy }),
            ...(sort?.sortDir && { sortDir: sort.sortDir }),
            ...(filters?.titleFilter && { titleFilter: filters.titleFilter }),
            ...(filters?.userNameFilter && { userNameFilter: filters.userNameFilter }),
            ...(filters?.userEmailFilter && { userEmailFilter: filters.userEmailFilter }),
        });
        const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/conversations?${params}`);
        return response.json();
    },

    async getRecentFeedback(page = 0, size = 20, sort?: SortParams, filters?: FeedbackFilters): Promise<PageResponse<RecentFeedbackDto>> {
        const params = new URLSearchParams({
            page: page.toString(),
            size: size.toString(),
            ...(sort?.sortBy && { sortBy: sort.sortBy }),
            ...(sort?.sortDir && { sortDir: sort.sortDir }),
            ...(filters?.type && { type: filters.type }),
            ...(filters?.userNameFilter && { userNameFilter: filters.userNameFilter }),
            ...(filters?.userEmailFilter && { userEmailFilter: filters.userEmailFilter }),
        });
        const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/feedback?${params}`);
        return response.json();
    },

    async getSystemStatistics(): Promise<SystemStatisticsDto> {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/statistics`);
        return response.json();
    },

    async getBackendVersion(): Promise<VersionInfoDto> {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/version`, {
            headers: {
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                'Pragma': 'no-cache',
                'Expires': '0'
            }
        });
        return response.json();
    },

    async getAvailableMetrics(): Promise<Record<string, { displayName: string; value: string; rawValue: number; unit: string }>> {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/metrics`);
        return response.json();
    },

    async getExternalIdStatistics(): Promise<ExternalIdStatisticsDto> {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/external-ids/statistics`);
        return response.json();
    },

    async searchExternalIds(entityType: string, query?: string, unmappedService?: string, notFoundService?: string, page = 0, size = 100): Promise<PageResponse<ExternalIdSearchResultDto>> {
        const params = new URLSearchParams({
            entityType,
            page: page.toString(),
            size: size.toString()
        });
        if (query) {
            params.set('query', query);
        }
        if (unmappedService) {
            params.set('unmappedService', unmappedService);
        }
        if (notFoundService) {
            params.set('notFoundService', notFoundService);
        }
        const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/external-ids/search?${params}`);
        return response.json();
    },

    async translateExternalId(request: TranslateExternalIdRequest): Promise<TranslateExternalIdResponse> {
        const token = authService.getToken();
        if (!token) {
            throw new Error('Not authenticated');
        }

        const response = await fetch(`${API_BASE_URL}/api/admin/external-ids/translate`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(request)
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return response.json();
    },

    async batchUpdateExternalIds(request: BatchUpdateExternalIdRequest): Promise<BatchUpdateExternalIdResponse> {
        const token = authService.getToken();
        if (!token) {
            throw new Error('Not authenticated');
        }

        const response = await fetch(`${API_BASE_URL}/api/admin/external-ids/batch-update`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(request)
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return response.json();
    },

    async triggerExternalIdPopulation(source: 'bricklink' | 'rebrickable', entityType?: string): Promise<{status: string; message: string; source: string; entityType?: string}> {
        const token = authService.getToken();
        if (!token) {
            throw new Error('Not authenticated');
        }

        const params = new URLSearchParams({ source });
        if (entityType) {
            params.append('entityType', entityType);
        }

        const response = await fetch(`${API_BASE_URL}/api/admin/external-ids/populate?${params.toString()}`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });

        if (!response.ok) {
            let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
            try {
                const errorBody = await response.text();
                if (errorBody) {
                    errorMessage += ` - ${errorBody}`;
                }
            } catch {
                // Ignore errors reading response body
            }
            throw new Error(errorMessage);
        }

        return response.json();
    },

    async triggerDataSync(): Promise<{status: string; message: string}> {
        const token = authService.getToken();
        if (!token) {
            throw new Error('Not authenticated');
        }

        const response = await fetch(`${API_BASE_URL}/api/admin/sync/data`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });

        if (!response.ok) {
            let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
            try {
                const errorBody = await response.text();
                if (errorBody) {
                    errorMessage += ` - ${errorBody}`;
                }
            } catch {
                // Ignore errors reading response body
            }
            throw new Error(errorMessage);
        }

        return response.json();
    },

    async getLogs(level?: string, search?: string, loggerFilter?: string, limit?: number): Promise<LogEntryDto[]> {
        const params = new URLSearchParams();
        if (level) params.append('level', level);
        if (search) params.append('search', search);
        if (loggerFilter) params.append('loggerFilter', loggerFilter);
        if (limit) params.append('limit', limit.toString());

        const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/logs?${params.toString()}`);
        return response.json();
    },

    async getLoggers(): Promise<LoggerInfoDto[]> {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/logs/loggers`);
        return response.json();
    },

    async setLoggerLevel(loggerName: string, level: SetLogLevelRequest['level']): Promise<LoggerInfoDto> {
        const token = authService.getToken();
        if (!token) {
            throw new Error('Not authenticated');
        }

        const response = await fetch(`${API_BASE_URL}/api/admin/logs/loggers/${encodeURIComponent(loggerName)}/level`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ level })
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return response.json();
    },

    async clearLogs(): Promise<{ message: string; cleared: number }> {
        const token = authService.getToken();
        if (!token) {
            throw new Error('Not authenticated');
        }

        const response = await fetch(`${API_BASE_URL}/api/admin/logs`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return response.json();
    },

    async getLogStats(): Promise<LogStatsDto> {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/logs/stats`);
        return response.json();
    },

    async getProperties(): Promise<PropertiesResponseDto> {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/properties`);
        return response.json();
    },

    async resolveProperty(key: string): Promise<import('./adminApi.types').PropertyResolutionDto> {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/properties/${encodeURIComponent(key)}/resolve`);
        return response.json();
    },

    async getExternalLinkStats(
        period: 'daily' | 'weekly' | 'monthly' = 'daily',
        startDate?: string,
        endDate?: string
    ): Promise<import('./adminApi.types').ExternalLinkStatsDto> {
        const params = new URLSearchParams({ period });
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);

        const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/external-link-stats?${params}`);
        return response.json();
    },

    // SQL Console
    async executeSqlQuery(query: string, confirmed?: boolean): Promise<SqlQueryResultDto> {
        const requestBody: { query: string; confirmed?: boolean } = { query };
        if (confirmed) {
            requestBody.confirmed = true;
        }
        const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/sql/query`, {
            method: 'POST',
            body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Backend always returns 200 with SqlQueryResultDto
        // Error details are in the 'error' field of the response
        return response.json();
    },

    // Saved SQL Scripts
    async getSavedScripts(): Promise<SavedSqlScriptDto[]> {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/sql/scripts`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
    },

    async createSavedScript(request: CreateSavedSqlScriptRequest): Promise<SavedSqlScriptDto> {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/sql/scripts`, {
            method: 'POST',
            body: JSON.stringify(request)
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
    },

    async updateSavedScript(id: number, request: UpdateSavedSqlScriptRequest): Promise<SavedSqlScriptDto> {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/sql/scripts/${id}`, {
            method: 'PUT',
            body: JSON.stringify(request)
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
    },

    async deleteSavedScript(id: number): Promise<void> {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/sql/scripts/${id}`, {
            method: 'DELETE'
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
    }
};

// Helper function to format LocalDateTime array to readable string
export function formatDateTime(dateArray: [number, number, number, number, number, number]): string {
    if (!dateArray || dateArray.length < 6) return 'N/A';
    const [year, month, day, hour, minute, second] = dateArray;
    const date = new Date(year, month - 1, day, hour, minute, second);
    return date.toLocaleString();
}
